import Num, { BigNumber } from '@flipster/utils/num';
import {
  sendChartTradeInteraction,
  type ChartTradeInteractionInput,
} from '@flipster/utils/simple-bridge/chart-trade-interaction';

import { TpSlDragIntent } from './tp-sl-drag-intent';

/**
 * 드래그한 가격을 종목의 tick size 배수로 맞춘다(ERD: "tick-aligned by web"). 데스크톱
 * `use-order-drag-mutation`과 동일하게 ROUND_HALF_DOWN. tickSize가 없거나 0 이하면 원가격 그대로.
 */
export function alignPriceToTick(price: number, tickSize?: number): number {
  if (!tickSize || tickSize <= 0) return price;
  return Num(price).div(tickSize).integerValue(BigNumber.ROUND_HALF_DOWN).times(tickSize).toNumber();
}

/**
 * Flow A: 차트 드래그로 새 TP/SL을 설정할 때, 모바일(webview) 경로의 처리.
 *
 * 데스크톱은 web이 모달을 직접 열지만(현행 유지), 모바일에서는 모달을 띄우는 단계를
 * native에 위임한다 — drop 시 `chartTradeInteraction`(CREATE_TPSL)을 simple bridge로 전송.
 * (결정: docs 11-9 / memory chart-tpsl-impl-split)
 *
 * payload는 ERD 스펙(symbol/slot/lineType/orderId/triggerPrice)만 보낸다. side/markPrice는
 * native가 slot/orderId + r230522 데이터로 알아내므로 보내지 않는다.
 */

/** 드래그 라인의 추가 컨텍스트(intent엔 없는, 차트/포지션에서 오는 값). */
export interface TpSlDragContext {
  symbol: string;
  slot: number;
  /** 종목 tick size. triggerPrice를 이 배수로 정렬해 전송한다. */
  tickSize?: number;
}

/** 드래그 intent + 컨텍스트를 CREATE_TPSL 인터랙션 입력으로 변환한다. */
export function tpSlDragCreateInput(intent: TpSlDragIntent, ctx: TpSlDragContext): ChartTradeInteractionInput {
  return {
    type: 'CREATE_TPSL',
    symbol: ctx.symbol,
    slot: ctx.slot,
    lineType: intent.kind, // 'TP' | 'SL'
    triggerPrice: String(alignPriceToTick(intent.triggerPrice, ctx.tickSize)),
  };
}

/**
 * webview일 때만 drop 인터랙션을 native로 전송한다.
 * @returns 전송했으면 true (모바일), 아니면 false (데스크톱 → 호출부가 web 모달 경로로 처리).
 */
export function sendTpSlDragCreateIfWebview(intent: TpSlDragIntent, ctx: TpSlDragContext, isWebview: boolean): boolean {
  if (!isWebview) return false;
  sendChartTradeInteraction(tpSlDragCreateInput(intent, ctx));
  return true;
}

/**
 * Flow B(수정)/D(삭제): 기존 TP/SL 라인을 드래그/취소할 때 native로 위임.
 *
 * 라인 콜백은 orderId(+드래그 시 새 가격)만 주므로, 열린 포지션 목록에서 해당 주문을 찾아
 * native가 필요로 하는 컨텍스트(symbol/slot/lineType)를 resolve한다.
 */

/** orderId로 주문이 속한 포지션을 찾아 만든 인터랙션 컨텍스트. */
export interface TpSlOrderContext {
  symbol: string;
  slot: number;
  lineType: 'TP' | 'SL';
}

/** resolve 입력으로 받는 포지션의 최소 형태 (r230522 OpenPosition이 구조적으로 충족). */
interface PositionWithTpSlOrders {
  symbol: string;
  slot: number;
  tpOrders: { orderId: string }[];
  slOrders: { orderId: string }[];
}

/** 열린 포지션들에서 orderId가 가리키는 TP/SL 주문의 컨텍스트를 찾는다. 없으면 undefined. */
export function resolveTpSlOrderContext(
  positions: PositionWithTpSlOrders[],
  orderId: string,
): TpSlOrderContext | undefined {
  for (const p of positions) {
    if (p.tpOrders.some((o) => o.orderId === orderId)) {
      return { symbol: p.symbol, slot: p.slot, lineType: 'TP' };
    }
    if (p.slOrders.some((o) => o.orderId === orderId)) {
      return { symbol: p.symbol, slot: p.slot, lineType: 'SL' };
    }
  }
  return undefined;
}

/** 라인을 새 가격으로 드래그 완료(Flow B). 주문을 찾으면 MODIFY_TPSL을 emit하고 true. */
export function sendTpSlModify(
  positions: PositionWithTpSlOrders[],
  orderId: string,
  newPrice: number,
  tickSize?: number,
): boolean {
  const ctx = resolveTpSlOrderContext(positions, orderId);
  if (!ctx) return false;
  sendChartTradeInteraction({
    type: 'MODIFY_TPSL',
    symbol: ctx.symbol,
    slot: ctx.slot,
    lineType: ctx.lineType,
    orderId,
    triggerPrice: String(alignPriceToTick(newPrice, tickSize)),
  });
  return true;
}

/** 라인 ×(취소)로 TP/SL 제거(Flow D). 주문을 찾으면 DELETE_TPSL을 emit하고 true. */
export function sendTpSlDelete(positions: PositionWithTpSlOrders[], orderId: string): boolean {
  const ctx = resolveTpSlOrderContext(positions, orderId);
  if (!ctx) return false;
  sendChartTradeInteraction({
    type: 'DELETE_TPSL',
    symbol: ctx.symbol,
    slot: ctx.slot,
    lineType: ctx.lineType,
    orderId,
  });
  return true;
}

/** 청산할 포지션의 최소 형태 (r230522 OpenPosition이 구조적으로 충족). */
export interface ClosablePosition {
  symbol: string;
  slot: number;
}

/** 포지션 라인 ×(청산)으로 전량 청산(Flow C). CLOSE_POSITION을 native로 emit한다. */
export function sendClosePosition(position: ClosablePosition): void {
  sendChartTradeInteraction({
    type: 'CLOSE_POSITION',
    symbol: position.symbol,
    slot: position.slot,
  });
}
